import { useMemo } from "react";
import { type AuditSession } from "../models/audit/auditSession";
import { Citation } from "../models/audit/citation";
import {
  getAttributeTestColumnCode,
  getColumnDisplayName,
} from "../lib/columnNameUtils";
import { attributeTestResultToCheckResult } from "../models/audit/attributeTestTranslator";
import { getIssueDisplayStatus } from "../pages/audit/auditWorkPaperViewV2/utils/issueDisplayStatus";
import { type CitationDocument } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";
import { type ControlSessionEnvelope } from "../models/audit/controlSession";
import { type ProcedureSessionTableShell } from "../models/audit/procedureSessionTableShell";
import { useHookCache } from "../lib/hookCache";
import { useControl } from "./audit/useControl";
import { useAttributeTestResultDetail } from "./audit/useAttributeTestResultDetail";
import { useSourceRow } from "./audit/useSourceRow";

interface UseAttributeTestDataProps {
  auditSession: AuditSession;
  checkResultId?: string;
  sourceRowId?: string;
  controlSession?: ControlSessionEnvelope | null;
}

/**
 * Workpaper-lazy-load: resolves the attribute-test side-panel data via the
 * per-detail hook `useAttributeTestResultDetail` instead of walking the
 * heavy `sourceRow.attributeTestResults` array that the previous flow
 * hydrated up-front.
 *
 * The legacy `attr-test-{sourceRowId}-{columnId}-{reviewOrder}` checkResultId
 * format is resolved through the new `attributeTestResultsSummary[]` shipped
 * on the dry source row in the table shell — that summary carries `id`,
 * `attributeTestColumnId`, and `reviewOrder`, which is exactly the
 * resolution we need before the detail hook fires.
 */
export function useAttributeTestData({
  auditSession,
  checkResultId,
  sourceRowId,
  controlSession,
}: UseAttributeTestDataProps) {
  // Envelope's `control` is `DryControl` (no `attributes`); use the bridge.
  const { data: hydratedControl } = useControl(controlSession?.controlId);
  const attributes =
    controlSession?.workStep?.attributes ?? hydratedControl?.attributes;
  const { getEntry } = useHookCache();

  // Workpaper-lazy-load: own both the row data and its in-flight flag here
  // so the side panel can gate its empty-state on a single combined
  // `isLoading`. `useHookCacheQuery` dedups against the parent
  // `ProcedureSessionWorkPaper`'s `useSourceRow` call (same cache key), so
  // this introduces no extra network request. The hook's internal `enabled`
  // gate handles the `!sourceRowId` case.
  const { data: sourceRow, isLoading: sourceRowLoading } = useSourceRow(
    auditSession.id,
    sourceRowId,
  );

  // The id-only steps below are mirrored by `selectionNamesAttributeTestResult`
  // in `models/audit/attributeTestTranslator`, which answers the same question
  // without the column and summary-row lookups. The two must stay in step.
  const attributeTestResultId = useMemo(() => {
    if (!checkResultId) return undefined;

    if (checkResultId.startsWith("attr-test-id-")) {
      return checkResultId.substring("attr-test-id-".length);
    }

    // Legacy `attr-test-{sourceRowId}-{columnId}-{reviewOrder}` shape — look
    // up `result.id` via the dry summary array shipped on the table-shell
    // row. The summary carries `attributeTestColumnId` + `reviewOrder`, so
    // we can resolve to the real id before the detail hook fires.
    if (!sourceRowId) return undefined;
    const prefix = `attr-test-${sourceRowId}-`;
    if (!checkResultId.startsWith(prefix)) return undefined;
    const remainder = checkResultId.substring(prefix.length);
    const lastDashIndex = remainder.lastIndexOf("-");
    if (lastDashIndex < 0) return undefined;
    const procedureColumnId = remainder.substring(0, lastDashIndex);
    const reviewOrder = parseInt(remainder.substring(lastDashIndex + 1), 10);
    if (Number.isNaN(reviewOrder)) return undefined;

    const column = auditSession.procedure.columns.find(
      (c) => c.id === procedureColumnId && c.attributeTestColumn,
    );
    if (!column?.attributeTestColumn) return undefined;

    // Workpaper-lazy-load: read the dry summary from the table-shell cache
    // (the synthesised `auditSession.sourceRows[]` carries the heavy row,
    // not the dry summary arrays).
    const tableShell = getEntry<ProcedureSessionTableShell>([
      "procedure-session",
      auditSession.id,
      "table-shell",
    ])?.data;
    const dryRow = tableShell?.sourceRows.find((r) => r.id === sourceRowId);
    const summaryMatch = dryRow?.attributeTestResultsSummary?.find(
      (r) =>
        r.attributeTestColumnId === column.attributeTestColumn!.id &&
        r.reviewOrder === reviewOrder,
    );
    return summaryMatch?.id;
  }, [
    checkResultId,
    sourceRowId,
    auditSession.id,
    auditSession.procedure.columns,
    getEntry,
  ]);

  const { data: attributeTestResult, isLoading: detailLoading } =
    useAttributeTestResultDetail(auditSession.id, attributeTestResultId);

  const column = useMemo(() => {
    if (!attributeTestResult) return undefined;
    return auditSession.procedure.columns.find(
      (c) =>
        c.attributeTestColumn?.id === attributeTestResult.attributeTestColumnId,
    );
  }, [attributeTestResult, auditSession.procedure.columns]);

  const columnName = useMemo(() => {
    return column
      ? getColumnDisplayName(
          column,
          auditSession.procedure.columns,
          attributes ?? [],
        )
      : "";
  }, [column, auditSession.procedure.columns, attributes]);

  // The attribute-test column's tickmark code (e.g. "A1" / "A2" when several
  // columns share attribute "A", or "B" for a solo column) — used as the
  // citation-row code prefix in the auditor trail ("A1.1", "B.1", …).
  const attributeColumnCode = useMemo(() => {
    if (!column) return undefined;
    return getAttributeTestColumnCode(
      column,
      auditSession.procedure.columns,
      attributes ?? [],
    );
  }, [column, auditSession.procedure.columns, attributes]);

  const allCitations = useMemo(() => {
    if (!attributeTestResult) return [];
    const citations: Citation[] = [];

    if (attributeTestResult.reperformanceSteps) {
      attributeTestResult.reperformanceSteps.forEach((step) => {
        // Prefer citation groups when available, fall back to flat citations
        if (step.citationGroups && step.citationGroups.length > 0) {
          const groupCitationIds = new Set<string>();
          step.citationGroups.forEach((group) => {
            group.citations.forEach((c) => groupCitationIds.add(c.id));
            citations.push(...group.citations);
          });
          // Also include any citations not yet assigned to a group
          // (e.g. newly created citations before a page refresh)
          if (step.citations) {
            step.citations.forEach((c) => {
              if (!groupCitationIds.has(c.id)) {
                citations.push(c);
              }
            });
          }
        } else if (step.citations) {
          citations.push(...step.citations);
        }
      });
    }

    return citations;
  }, [attributeTestResult]);

  const filteredCitations = useMemo(() => {
    return allCitations.filter((citation) => citation.isKey === true);
  }, [allCitations]);

  const citationDocuments = useMemo((): CitationDocument[] => {
    if (allCitations.length === 0) return [];
    const flat: CitationDocument[] = [];
    const documentMap = new Map<string, CitationDocument>();

    allCitations.forEach((citation) => {
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === citation.documentId,
      );
      if (sourceDocument) {
        const existing = documentMap.get(citation.documentId);
        if (existing) {
          existing.citations.push(citation);
        } else {
          const entry: CitationDocument = {
            document: sourceDocument,
            citations: [citation],
          };
          documentMap.set(citation.documentId, entry);
          flat.push(entry);
        }
      }
    });

    return flat;
  }, [allCitations, auditSession.documents]);

  const displayStatus = useMemo(() => {
    if (!attributeTestResult || !column || !sourceRowId) return "no_issues";
    const checkResult = attributeTestResultToCheckResult(
      attributeTestResult,
      column,
      sourceRowId,
    );
    return getIssueDisplayStatus(checkResult.issues);
  }, [attributeTestResult, column, sourceRowId]);

  return {
    attributeTestResultId,
    sourceRow,
    attributeTestResult,
    isLoading: sourceRowLoading || detailLoading,
    column,
    columnName,
    attributeColumnCode,
    allCitations,
    filteredCitations,
    citationDocuments,
    displayStatus,
  };
}
